export type Bucket =
  | "fire" | "flood" | "earthquake" | "conflict" | "other"
  | "traffic" | "storm" | "snow" | "ice" | "heat" | "fog" | "hazmat" | "outage";

export const BUCKETS: Bucket[] = [
  "fire", "flood", "earthquake", "conflict", "other",
  "traffic", "storm", "snow", "ice", "heat", "fog", "hazmat", "outage",
];

/** Buckets treated as "quiet by default" for PUSH (server alert-dispatcher mirrors this set
 *  in Go). In-app they are NOT quiet — see defaultBucketLevel. */
export const ADVISORY_BUCKETS: Bucket[] = ["traffic", "storm", "snow", "ice", "heat", "fog"];

/** Default IN-APP visibility threshold for a bucket. Uniform "warning": every hazard —
 *  advisories included — shows in-app at warning+, so a Moderate advisory (e.g. an active
 *  heat-wave) is SEEN rather than silently dropped. The advisory "quiet by default" rule is
 *  intentionally kept PUSH-side only (server alert-dispatcher DefaultBucketLevel: advisory =
 *  critical), making Moderate advisories visible-in-app-but-not-pushed. Do NOT re-add an
 *  advisory=critical branch here — that re-breaks the in-app heat-wave case. Per-bucket user
 *  overrides (settings.alertLevel) still apply to both surfaces. */
export function defaultBucketLevel(_bucket: string): "off" | "info" | "warning" | "critical" {
  return "warning";
}

const has = (s: string, ...subs: string[]) => subs.some((x) => s.includes(x));

/** Group a CAP classification (category + free-text event) down to a display bucket.
 *  Reuses legacy bucket names so existing i18n/maps apply; only adds new kinds. */
export function displayBucket(category?: string, event?: string): Bucket {
  const e = (event ?? "").toLowerCase().trim();
  switch (category) {
    case "Fire": return "fire";
    case "Geo":
      if (has(e, "landslide", "slide")) return "other";
      return "earthquake"; // earthquake/quake/seismic and Geo default
    case "Met":
      if (has(e, "flood", "rain", "regen", "thaw", "tau", "hochwasser")) return "flood";
      if (has(e, "thunder", "gewitter", "storm", "wind", "gale", "orkan", "sturm")) return "storm";
      if (has(e, "snow", "schnee")) return "snow";
      if (has(e, "ice", "frost", "glaze", "glatteis", "glätte")) return "ice";
      if (has(e, "heat", "hitze")) return "heat";
      if (has(e, "fog", "nebel")) return "fog";
      return "storm"; // Met default
    case "Transport": return "traffic";
    case "CBRNE": case "Env": return "hazmat";
    case "Infra": return "outage";
    case "Security": case "Safety": case "Rescue": return "conflict";
    default: return "other"; // Health, Other, unknown
  }
}
